/**
 * Client-side validation schemas using Zod
 *
 * Mirrors backend Pydantic validation rules for immediate user feedback.
 * Server-side validation is still performed for security.
 */

import { z } from 'zod';

/**
 * Tag validation regex - alphanumeric, hyphens, and underscores only
 */
const TAG_REGEX = /^[a-zA-Z0-9_-]+$/;

/**
 * Validation schema for creating/updating a project
 */
export const projectSchema = z.object({
  name: z
    .string()
    .min(1, 'Name is required')
    .max(200, 'Name must be 200 characters or less'),

  description: z
    .string()
    .max(1000, 'Description must be 1000 characters or less')
    .optional()
    .or(z.literal('')),

  instruction: z
    .string()
    .min(1, 'Instruction is required')
    .max(50000, 'Instruction must be 50,000 characters or less'),

  endpoint: z
    .string()
    .max(500, 'Endpoint URL must be 500 characters or less')
    .url('Must be a valid URL')
    .optional()
    .or(z.literal('')),

  critical_keys: z
    .array(z.string())
    .max(20, 'Maximum 20 critical keys allowed')
    .optional()
    .default([]),

  tags: z
    .array(
      z
        .string()
        .max(30, 'Each tag must be 30 characters or less')
        .regex(TAG_REGEX, 'Tags can only contain letters, numbers, hyphens, and underscores')
    )
    .max(10, 'Maximum 10 tags allowed'),

  status: z.enum(['draft', 'active', 'archived'], {
    errorMap: () => ({ message: 'Status must be draft, active, or archived' }),
  }),

  browser_type: z.enum(['tetra', 'tetra-anchor', 'stealth', 'headless', 'kernel'], {
    errorMap: () => ({ message: 'Invalid browser type' }),
  }),

  use_proxy: z.boolean().default(false),
});

export type ProjectFormData = z.infer<typeof projectSchema>;

/**
 * Validate a single tag
 */
export function validateTag(tag: string): string | null {
  if (tag.length === 0) {
    return 'Tag cannot be empty';
  }
  if (tag.length > 30) {
    return 'Tag must be 30 characters or less';
  }
  if (!TAG_REGEX.test(tag)) {
    return 'Tag can only contain letters, numbers, hyphens, and underscores';
  }
  return null;
}
